// The Forge — research: ten points to share, each unit researched up to three times. Rows expand to the stat change and the
// one crimson RESEARCH button; the research queue sits above with cancel.
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { art } from "@/kit/art";
import { CostLine, Countdown, Pips, RowAction, ScreenHero, TimeBar, UnitMedal } from "@/kit/city";
import { IconChevronRight, IconClose } from "@/kit/icons";
import { Confirm } from "@/kit/overlays";
import { Button, Card, CardHead, Disc, Empty, Row, RowText, cx } from "@/kit/primitives";
import { BUILDING_BLURB, TROOP_KEYS, TROOP_NAMES, type TroopKey } from "@/game/constants";
import { fmtNum, plural } from "@/game/format";
import { fmtDuration, parseHMS } from "@/game/time";
import { unitStats, WORLD } from "@/game/rules";
import { buildingMap, researchLevel, researchPoints, researchQueue, shortfall, techFor, type ResearchEntry } from "@/game/city";
import { CityPage, useCity } from "./CityRoute";

export function Forge() {
  const { v, wid, vid, actions, base } = useCity();
  const nav = useNavigate();
  const [open, setOpen] = useState<TroopKey | null>(null);
  const [cancel, setCancel] = useState<ResearchEntry | null>(null);
  const queue = useMemo(() => (v ? researchQueue(v) : []), [v]);
  if (!v) return <CityPage>{null}</CityPage>;
  const forge = buildingMap(v).b_s;
  const pts = researchPoints(v);
  const free = Math.max(0, pts.max - pts.used);
  const units = TROOP_KEYS.filter((k) => k !== "t_n" && !!techFor(v, k));
  const maxedCount = units.filter((k) => researchLevel(v, k) >= WORLD.research.maxLevel).length;
  const queueFull = queue.length >= WORLD.queue.research + (v.active_effects?.extend_construction_queue ? WORLD.queue.extender : 0);

  return (
    <CityPage>
      <div className="stack">
        <ScreenHero
          level={forge?.level}
          title="Forge"
          blurb={BUILDING_BLURB.b_s}
          art={art.hero.forge}
          artWidth="50%"
          onBack={() => nav(base)}
          stats={[
            { label: "Points used", value: pts.used, small: `of ${pts.max}` },
            { label: "Free", value: free, tone: free ? ("green" as const) : undefined, small: free === 1 ? "point" : "points" },
            { label: "Maxed", value: maxedCount, small: maxedCount === 1 ? "unit" : "units" },
          ]}
        />

        {queue.length > 0 && (
          <Card>
            <CardHead title="Researching" aside={plural(queue.length, "job")} />
            <div className="rows">
              {queue.map((q, i) => (
                <Row key={`${q.item.name}-${i}`} className={cx(i === 0 && "goldedge")} style={{ flexWrap: "wrap" }}>
                  {q.troop && <UnitMedal troop={q.troop} tier={q.item.level} size={56} />}
                  <RowText line={<><b className="creamt" style={{ fontWeight: 600, fontFamily: "var(--title)", letterSpacing: ".06em", textTransform: "uppercase" }}>{q.item.name}</b> <span className="gold">Level {q.item.level}</span>{q.item.is_upgrade ? null : <span className="crimtxt"> · lowering</span>}</>} meta={i === 0 ? "Researching now" : "Queued"} />
                  <div className="right">
                    <div className="val" style={{ font: "600 var(--fs-value)/1 var(--text)" }}>{i === 0 ? <Countdown endMs={q.endMs} /> : fmtDuration(q.durationMs)}</div>
                    <Disc size="sm" label={`Cancel ${q.item.name} level ${q.item.level}`} onClick={() => setCancel(q)}><IconClose /></Disc>
                  </div>
                  {i === 0 && <div className="row-body"><TimeBar startMs={q.endMs - q.durationMs} endMs={q.endMs} /></div>}
                </Row>
              ))}
            </div>
            <div className="c-note left">Cancelling a job also cancels the jobs after it; they are re-queued in order.</div>
          </Card>
        )}

        <Card>
          <CardHead title="Research" aside={free ? `${free} point${free === 1 ? "" : "s"} free` : "all points allocated"}>
            <Pips level={pts.used} max={pts.max} />
          </CardHead>
          <div className="rows">
            {units.map((k) => {
              const tech = techFor(v, k)!;
              const level = tech.level;
              const nextLvl = Math.min(WORLD.research.maxLevel, level + 1 + queue.filter((q) => q.troop === k).length);
              const maxed = level >= WORLD.research.maxLevel;
              const isOpen = open === k;
              const locked = level === 0;
              return (
                <Row key={k} className={cx(isOpen && "expanded")} style={{ flexWrap: "wrap" }}>
                  <button type="button" className="rowhit" onClick={() => setOpen(isOpen ? null : k)} aria-expanded={isOpen}>
                    <IconChevronRight className="chev" />
                    <UnitMedal troop={k} tier={Math.max(1, level)} size={60} dim={locked} locked={locked} />
                    <RowText line={<b className={cx(locked ? "muted" : "creamt")} style={{ fontWeight: 600, fontFamily: "var(--title)", letterSpacing: ".08em", textTransform: "uppercase" }}>{TROOP_NAMES[k]}</b>} />
                    <Pips level={level} boxes />
                    <span className={cx(locked ? "muted" : "gold")} style={{ font: "400 var(--fs-body2)/1 var(--text)", minWidth: "calc(var(--u) * 70)" }}>Level {level}</span>
                  </button>
                  <div className="right">{maxed ? <RowAction kind="max" /> : <RowAction kind="plus" label={`Research ${TROOP_NAMES[k]}`} onClick={() => setOpen(isOpen ? null : k)} />}</div>
                  {isOpen && (
                    <div className="row-body stack" style={{ gap: "calc(var(--u) * 12)" }}>
                      <StatDelta k={k} from={level} to={Math.min(3, Math.max(1, nextLvl))} locked={locked} />
                      {!maxed && (() => {
                        const cost = { r_w: tech.r_w, r_c: tech.r_c, r_i: tech.r_i, time: parseHMS(tech.time) };
                        const s = shortfall(v, cost);
                        let reason: string | null = null;
                        if (!tech.has_prereqs) reason = tech.prereqs_string?.replace(/^Required:\s*/, "Requires ") || "Requirements not met";
                        else if (!free) reason = "No research points free — lower another unit to free one";
                        else if (queueFull) reason = "Research queue is full";
                        else if (Object.keys(s.short).length) reason = `Not enough resources in ${v.village?.city_name}`;
                        return (
                          <>
                            <CostLine cost={cost} time={cost.time} short={s.short} size="sm" />
                            <Button kind="crimson" wide disabled={!!reason} busy={actions.isBusy(`research:${k}`)}
                              onClick={() => actions.run(`research:${k}`, () => api.research.queue({ wid, vid }, tech.key), { success: `${TROOP_NAMES[k]} research queued.` })}>
                              Research level {["", "I", "II", "III"][nextLvl] ?? nextLvl} · 1 point
                            </Button>
                            {reason && <div className="c-note">{reason}</div>}
                          </>
                        );
                      })()}
                      {level > 0 && (
                        <Button kind="ghost" size="sm" busy={actions.isBusy(`revoke:${k}`)}
                          onClick={() => actions.run(`revoke:${k}`, () => api.research.revoke({ wid, vid }, tech.key), { success: `Lowering ${TROOP_NAMES[k]} to level ${level - 1} — the point frees when it finishes.` })}>
                          Lower to level {level - 1} · frees 1 point
                        </Button>
                      )}
                    </div>
                  )}
                </Row>
              );
            })}
            {!units.length && <Empty>The game sent no research list for this city.</Empty>}
          </div>
        </Card>
      </div>

      <Confirm
        open={!!cancel}
        onClose={() => setCancel(null)}
        title="Cancel this research?"
        text={cancel ? `${cancel.item.name} level ${cancel.item.level} is removed and its resources come back. Anything queued after it is re-queued.` : ""}
        action="Cancel research"
        busy={actions.isBusy("research_cancel")}
        onConfirm={async () => {
          if (!cancel) return;
          const r = await actions.run("research_cancel", () => api.research.cancel({ wid, vid }, cancel.index));
          if (r) setCancel(null);
        }}
      />
    </CityPage>
  );
}

function StatDelta({ k, from, to, locked }: { k: TroopKey; from: number; to: number; locked: boolean }) {
  const a = unitStats(k, Math.max(1, from));
  const b = unitStats(k, to);
  const cell = (label: string, x: number, y: number) => (
    <div className="c-tile" style={{ padding: "calc(var(--u) * 12) calc(var(--u) * 6)" }}>
      <div className="lbl">{label}</div>
      <div className="val" style={{ fontSize: "calc(var(--fs-value) * 1.05)" }}>
        {locked ? <span className="muted">—</span> : <span className="creamt">{fmtNum(x)}</span>} <span className="gold" style={{ fontSize: ".7em" }}>→</span> <span className="gold">{fmtNum(y)}</span>
      </div>
      {!locked && y > x && <div className="delta">+{fmtNum(y - x)}</div>}
    </div>
  );
  return (
    <div className="c-grid3">
      {cell("Attack", a.att, b.att)}
      {cell("Def · infantry", a.infdef, b.infdef)}
      {cell("Def · cavalry", a.cavdef, b.cavdef)}
    </div>
  );
}
